/**
 * ImportFieldRules — custom field mapping rules step in the bulk import flow.
 * Lets users define rules like:
 *   IF [source field] [condition] [value] THEN SET [target field] TO [target value]
 *
 * Rules are applied to entries in order, modifying the data before preview.
 */
import { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { LookupFieldSelect } from './LookupFieldSelect';
import {
  saveImportConfigForBrand,
  type PersistedFieldRule,
  type PersistedExclusionRule,
} from '../../services/firebase';
import toast from 'react-hot-toast';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FieldRule {
  id: string;
  sourceField: string;
  condition: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'not_contains' | 'is_empty' | 'is_not_empty';
  value: string;
  targetField: string;
  targetValue: string;
  enabled: boolean;
}

const SOURCE_FIELDS = [
  { value: 'reference', label: 'Reference' },
  { value: 'title', label: 'Title' },
  { value: 'productName', label: 'Product Name' },
  { value: 'productType', label: 'Product Type' },
  { value: 'description', label: 'Description' },
  { value: 'variant', label: 'Variant' },
  { value: 'movement', label: 'Movement' },
  { value: 'tags', label: 'Tags' },
  { value: 'model', label: 'Model' },
  { value: 'collection', label: 'Collection' },
  { value: 'caseMaterial', label: 'Case Material' },
  { value: 'dialColor', label: 'Dial Color' },
];

const CONDITIONS = [
  { value: 'contains', label: 'contains' },
  { value: 'equals', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

interface TargetFieldDef {
  value: string;
  label: string;
  type: 'text' | 'lookup' | 'boolean' | 'multi';
  lookupCollection?: string;
  group: string;
}

const TARGET_FIELDS: TargetFieldDef[] = [
  // Basic Info
  { value: 'collection', label: 'Collection', type: 'text', group: 'Basic Info' },
  { value: 'model', label: 'Model', type: 'text', group: 'Basic Info' },
  { value: 'bezelType', label: 'Bezel Type', type: 'lookup', lookupCollection: 'lookup_bezel_types', group: 'Basic Info' },
  { value: 'bezelMaterial', label: 'Bezel Material', type: 'lookup', lookupCollection: 'lookup_bezel_material', group: 'Basic Info' },
  { value: 'bezelColors', label: 'Bezel Colors', type: 'lookup', lookupCollection: 'lookup_bezel_colors', group: 'Basic Info' },
  { value: 'caseColors', label: 'Case Colors', type: 'lookup', lookupCollection: 'lookup_case_colors', group: 'Basic Info' },
  { value: 'crownType', label: 'Crown Type', type: 'lookup', lookupCollection: 'lookup_crown_types', group: 'Basic Info' },
  // Movement
  { value: 'calibre', label: 'Calibre', type: 'text', group: 'Movement' },
  { value: 'movement', label: 'Base Movement', type: 'text', group: 'Movement' },
  // Digital-watch module identifier — the electronic counterpart of calibre.
  // A rule setting `module` flows into the Module extraction step / save-time
  // module link (findModulesByLegacyValues → moduleId), mirroring calibre.
  { value: 'module', label: 'Module (digital)', type: 'text', group: 'Movement' },
  { value: 'movementType', label: 'Movement Type', type: 'lookup', lookupCollection: 'lookup_movement_types', group: 'Movement' },
  { value: 'jewels', label: 'Jewels', type: 'text', group: 'Movement' },
  { value: 'isChronograph', label: 'Chronograph', type: 'boolean', group: 'Movement' },
  { value: 'isGMT', label: 'GMT', type: 'boolean', group: 'Movement' },
  { value: 'chronometer', label: 'Chronometer', type: 'boolean', group: 'Movement' },
  { value: 'manualWinding', label: 'Manual Winding', type: 'boolean', group: 'Movement' },
  { value: 'quickSetDate', label: 'Quick Set Date', type: 'boolean', group: 'Movement' },
  { value: 'isInHouse', label: 'In-House', type: 'boolean', group: 'Movement' },
  { value: 'columnWheel', label: 'Column Wheel', type: 'boolean', group: 'Movement' },
  { value: 'doubleBarrel', label: 'Double Barrel', type: 'boolean', group: 'Movement' },
  { value: 'functions', label: 'Functions (comma-separated)', type: 'multi', group: 'Movement' },
  // Case
  { value: 'caseSize', label: 'Case Size (mm)', type: 'text', group: 'Case' },
  { value: 'caseHeight', label: 'Case Thickness (mm)', type: 'text', group: 'Case' },
  { value: 'lugWidth', label: 'Lug Width (mm)', type: 'text', group: 'Case' },
  { value: 'caseMaterial', label: 'Case Material', type: 'lookup', lookupCollection: 'lookup_case_materials', group: 'Case' },
  // A sealed case that stays sealed as ambient pressure DROPS — the opposite
  // direction from water resistance. A boolean, not a rating: see
  // CaseInfo.isLowPressureResistant in shared-types.
  { value: 'isLowPressureResistant', label: 'Low Pressure Resistant', type: 'boolean', group: 'Case' },
  { value: 'waterRes', label: 'Water Resistance', type: 'lookup', lookupCollection: 'lookup_water_resistance', group: 'Case' },
  { value: 'caseFinish', label: 'Case Finish', type: 'lookup', lookupCollection: 'lookup_case_finishes', group: 'Case' },
  { value: 'caseCoating', label: 'Case Coating', type: 'lookup', lookupCollection: 'lookup_case_coatings', group: 'Case' },
  { value: 'caseBack', label: 'Case Back', type: 'lookup', lookupCollection: 'lookup_caseback_type', group: 'Case' },
  // Dial & Hands
  { value: 'dialColor', label: 'Dial Color', type: 'lookup', lookupCollection: 'lookup_dial_colors', group: 'Dial & Hands' },
  { value: 'dialFinish', label: 'Dial Finish', type: 'lookup', lookupCollection: 'lookup_dial_finish', group: 'Dial & Hands' },
  { value: 'hands', label: 'Hands', type: 'lookup', lookupCollection: 'lookup_dial_hands', group: 'Dial & Hands' },
  { value: 'crystal', label: 'Crystal', type: 'lookup', lookupCollection: 'lookup_crystal_materials', group: 'Dial & Hands' },
  { value: 'lumePresence', label: 'Lume Presence', type: 'lookup', lookupCollection: 'lookup_lume_presence', group: 'Dial & Hands' },
  { value: 'lumeMaterial', label: 'Lume Material', type: 'lookup', lookupCollection: 'lookup_lume_material', group: 'Dial & Hands' },
  { value: 'lumeDayColor', label: 'Lume Day Color', type: 'lookup', lookupCollection: 'lookup_lume_day_color', group: 'Dial & Hands' },
  { value: 'lumeGlowColor', label: 'Lume Glow Color', type: 'lookup', lookupCollection: 'lookup_lume_glow_color', group: 'Dial & Hands' },
  { value: 'subdialTypes', label: 'Subdial Types', type: 'lookup', lookupCollection: 'lookup_dial_subdials', group: 'Dial & Hands' },
  { value: 'chapterRing', label: 'Chapter Ring', type: 'lookup', lookupCollection: 'lookup_chapter_ring_type', group: 'Dial & Hands' },
  { value: 'minuteTrack', label: 'Minute Track', type: 'lookup', lookupCollection: 'lookup_minute_track_type', group: 'Dial & Hands' },
  { value: 'dialScales', label: 'Dial Scales', type: 'lookup', lookupCollection: 'lookup_dial_scales', group: 'Dial & Hands' },
  { value: 'dialFeatures', label: 'Dial Features', type: 'lookup', lookupCollection: 'lookup_dial_features', group: 'Dial & Hands' },
  { value: 'logoType', label: 'Logo Type', type: 'lookup', lookupCollection: 'lookup_dial_logo_type', group: 'Dial & Hands' },
  // Strap / Bracelet
  { value: 'strapMaterial', label: 'Strap Material', type: 'lookup', lookupCollection: 'lookup_strap_materials', group: 'Strap / Bracelet' },
  { value: 'strapColor', label: 'Strap Color', type: 'lookup', lookupCollection: 'lookup_strap_color', group: 'Strap / Bracelet' },
  // Production
  { value: 'isLimited', label: 'Limited Edition', type: 'boolean', group: 'Production' },
  // Two names for one number. `limitedQty` is this importer's original rule
  // field; `limitedEditionCount` is what the enrichment prompt offers and what
  // ExtractedWatch carries since shared-types v1.124.0. BulkImport reads
  // whichever is present, so both stay selectable.
  { value: 'limitedEditionCount', label: 'Limited Edition Size', type: 'text', group: 'Production' },
  { value: 'limitedQty', label: 'Limited Edition Quantity (legacy)', type: 'text', group: 'Production' },
  { value: 'specialEdition', label: 'Special Edition', type: 'boolean', group: 'Production' },
];

// ── Rule evaluation ───────────────────────────────────────────────────────────

function evaluateCondition(fieldValue: string, condition: FieldRule['condition'], ruleValue: string): boolean {
  const fv = (fieldValue || '').toLowerCase();
  const rv = ruleValue.toLowerCase();
  switch (condition) {
    case 'contains': return fv.includes(rv);
    case 'equals': return fv === rv;
    case 'starts_with': return fv.startsWith(rv);
    case 'ends_with': return fv.endsWith(rv);
    case 'not_contains': return !fv.includes(rv);
    case 'is_empty': return !fv || fv.trim() === '';
    case 'is_not_empty': return !!fv && fv.trim() !== '';
    default: return false;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyRules<T extends Record<string, any>>(entries: T[], rules: FieldRule[], overwrite = false): { entries: T[]; matchCounts: Map<string, number> } {
  const matchCounts = new Map<string, number>();
  rules.forEach((r) => matchCounts.set(r.id, 0));

  const result = entries.map((entry) => {
    const modified = { ...entry };
    for (const rule of rules) {
      if (!rule.enabled) continue;
      const sourceValue = String(entry[rule.sourceField] || '');
      if (evaluateCondition(sourceValue, rule.condition, rule.value)) {
        // Only set value if overwrite is on, or the target field is empty
        const existing = (modified as Record<string, unknown>)[rule.targetField];
        if (overwrite || !existing || (typeof existing === 'string' && !existing.trim())) {
          (modified as Record<string, unknown>)[rule.targetField] = rule.targetValue;
        }
        matchCounts.set(rule.id, (matchCounts.get(rule.id) || 0) + 1);
      }
    }
    return modified;
  });

  return { entries: result, matchCounts };
}

// ── Exclusion rules ──────────────────────────────────────────────────────────

export interface ExclusionRule {
  id: string;
  sourceField: string;
  condition: FieldRule['condition'];
  value: string;
  enabled: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyExclusionRules<T extends Record<string, any>>(entries: T[], rules: ExclusionRule[]): { excludedIndices: Set<number>; matchCounts: Map<string, number> } {
  const matchCounts = new Map<string, number>();
  rules.forEach((r) => matchCounts.set(r.id, 0));
  const excludedIndices = new Set<number>();

  entries.forEach((entry, idx) => {
    for (const rule of rules) {
      if (!rule.enabled) continue;
      const sourceValue = String(entry[rule.sourceField] || '');
      if (evaluateCondition(sourceValue, rule.condition, rule.value)) {
        excludedIndices.add(idx);
        matchCounts.set(rule.id, (matchCounts.get(rule.id) || 0) + 1);
      }
    }
  });

  return { excludedIndices, matchCounts };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ExtractionRuleForSave {
  sourceField: string;
  pattern: string;
  replaceWith?: string;
  targetField?: 'calibre' | 'movement' | 'module';
}

interface ImportFieldRulesProps {
  entryCount: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entries: Record<string, any>[];
  brandId: string;
  initialRules?: FieldRule[];
  initialExclusionRules?: ExclusionRule[];
  extractionRules?: ExtractionRuleForSave[];
  productTypes?: string[];
  onApply: (rules: FieldRule[], exclusionRules: ExclusionRule[], allFieldRules: FieldRule[], allExclusionRules: ExclusionRule[]) => void;
  onBack: () => void;
  onSkip: () => void;
}

let ruleIdCounter = 0;
function newRule(): FieldRule {
  return {
    id: `rule-${++ruleIdCounter}`,
    sourceField: 'title',
    condition: 'contains',
    value: '',
    targetField: 'movementType',
    targetValue: '',
    enabled: true,
  };
}

let exclusionIdCounter = 0;
function newExclusionRule(): ExclusionRule {
  return {
    id: `excl-${++exclusionIdCounter}`,
    sourceField: 'title',
    condition: 'contains',
    value: '',
    enabled: true,
  };
}

export function ImportFieldRules({ entryCount, entries, brandId, initialRules, initialExclusionRules, extractionRules: extractionRulesForSave, productTypes, onApply, onBack, onSkip }: ImportFieldRulesProps) {
  const [rules, setRules] = useState<FieldRule[]>([]);
  const [exclusionRules, setExclusionRules] = useState<ExclusionRule[]>([]);
  const [matchCounts, setMatchCounts] = useState<Map<string, number>>(new Map());
  const [tested, setTested] = useState(false);
  const [loadingRules, setLoadingRules] = useState(true);
  const [showJson, setShowJson] = useState(false);
  const [jsonLimit, setJsonLimit] = useState(10);

  // Load pre-loaded rules from parent (already fetched from Firestore)
  useEffect(() => {
    if (initialRules && initialRules.length > 0 && rules.length === 0) {
      setRules(initialRules);
      toast.success(`Loaded ${initialRules.length} saved rule${initialRules.length !== 1 ? 's' : ''}`);
    }
    if (initialExclusionRules && initialExclusionRules.length > 0 && exclusionRules.length === 0) {
      setExclusionRules(initialExclusionRules);
    }
    setLoadingRules(false);
  }, [initialRules, initialExclusionRules]); // eslint-disable-line react-hooks/exhaustive-deps

  const addRule = () => setRules([...rules, newRule()]);

  const updateRule = (id: string, updates: Partial<FieldRule>) => {
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, ...updates } : r));
    setTested(false);
  };

  const removeRule = (id: string) => {
    setRules(rules.filter((r) => r.id !== id));
  };

  const addExclusionRule = () => setExclusionRules([...exclusionRules, newExclusionRule()]);

  const updateExclusionRule = (id: string, updates: Partial<ExclusionRule>) => {
    setExclusionRules((prev) => prev.map((r) => r.id === id ? { ...r, ...updates } : r));
    setTested(false);
  };

  const removeExclusionRule = (id: string) => {
    setExclusionRules(exclusionRules.filter((r) => r.id !== id));
  };

  const handleTest = () => {
    const enabledRules = rules.filter((r) => r.enabled);
    const { matchCounts: fieldCounts } = applyRules(entries, enabledRules);
    const enabledExclusions = exclusionRules.filter((r) => r.enabled);
    const { matchCounts: exclusionCounts } = applyExclusionRules(entries, enabledExclusions);
    const combined = new Map([...fieldCounts, ...exclusionCounts]);
    setMatchCounts(combined);
    setTested(true);
  };

  const handleApply = async () => {
    const enabledRules = rules.filter((r) => r.enabled && r.targetValue.trim());

    // Save field rules, extraction rules, AND exclusion rules for this brand
    try {
      const fieldRulesToSave: PersistedFieldRule[] = rules.map(({ sourceField, condition, value, targetField, targetValue, enabled }) =>
        ({ sourceField, condition, value, targetField, targetValue, enabled }));
      const extractionToSave = (extractionRulesForSave || []).map(({ sourceField, pattern, replaceWith, targetField }) => ({
        sourceField, pattern,
        ...(replaceWith ? { replaceWith } : {}),
        ...(targetField && targetField !== 'calibre' ? { targetField } : {}),
      }));
      const exclusionToSave: PersistedExclusionRule[] = exclusionRules.map(({ sourceField, condition, value, enabled }) =>
        ({ sourceField, condition, value, enabled }));
      await saveImportConfigForBrand(brandId, fieldRulesToSave, extractionToSave, exclusionToSave);
    } catch {
      // non-fatal — rules still apply even if save fails
    }

    onApply(enabledRules, exclusionRules.filter((r) => r.enabled), rules, exclusionRules);
  };

  const noValue = (condition: string) => condition === 'is_empty' || condition === 'is_not_empty';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Define rules to populate fields based on conditions.
            Rules apply to all <span className="font-medium">{entryCount}</span> entries.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Example: IF title contains "Solar" THEN SET movementType TO "quartz"
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onBack}>Back</Button>
      </div>

      {/* Rules list */}
      <div className="space-y-3">
        {rules.map((rule) => (
          <div key={rule.id} className={`border rounded-lg p-3 space-y-2 ${!rule.enabled ? 'opacity-40' : ''}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-6">IF</span>
              <select
                value={rule.sourceField}
                onChange={(e) => updateRule(rule.id, { sourceField: e.target.value })}
                className="text-sm border rounded px-2 py-1"
              >
                {SOURCE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select
                value={rule.condition}
                onChange={(e) => updateRule(rule.id, { condition: e.target.value as FieldRule['condition'] })}
                className="text-sm border rounded px-2 py-1"
              >
                {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {!noValue(rule.condition) && (
                rule.sourceField === 'productType' && productTypes && productTypes.length > 0 ? (
                  <select
                    value={rule.value}
                    onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                    className="text-sm border rounded px-2 py-1 flex-1"
                  >
                    <option value="">Select type...</option>
                    {productTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={rule.value}
                    onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                    placeholder="value..."
                    className="text-sm border rounded px-2 py-1 flex-1 min-w-[100px]"
                  />
                )
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-6">SET</span>
              <select
                value={rule.targetField}
                onChange={(e) => updateRule(rule.id, { targetField: e.target.value, targetValue: '' })}
                className="text-sm border rounded px-2 py-1"
              >
                {(() => {
                  const groups = [...new Set(TARGET_FIELDS.map((f) => f.group))];
                  return groups.map((g) => (
                    <optgroup key={g} label={g}>
                      {TARGET_FIELDS.filter((f) => f.group === g).map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
              <span className="text-xs text-gray-500">TO</span>
              {(() => {
                const fieldDef = TARGET_FIELDS.find((f) => f.value === rule.targetField);
                if (fieldDef?.type === 'lookup' && fieldDef.lookupCollection) {
                  return (
                    <div className="flex-1 min-w-[150px]">
                      <LookupFieldSelect
                        label=""
                        value={rule.targetValue}
                        lookupCollection={fieldDef.lookupCollection}
                        onChange={(v) => updateRule(rule.id, { targetValue: v })}
                        placeholder="Select..."
                      />
                    </div>
                  );
                }
                if (fieldDef?.type === 'boolean') {
                  return (
                    <select
                      value={rule.targetValue}
                      onChange={(e) => updateRule(rule.id, { targetValue: e.target.value })}
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes / True</option>
                      <option value="false">No / False</option>
                    </select>
                  );
                }
                if (fieldDef?.type === 'multi') {
                  return (
                    <input
                      type="text"
                      value={rule.targetValue}
                      onChange={(e) => updateRule(rule.id, { targetValue: e.target.value })}
                      placeholder="value1, value2, value3..."
                      className="text-sm border rounded px-2 py-1 flex-1 min-w-[150px]"
                    />
                  );
                }
                return (
                  <input
                    type="text"
                    value={rule.targetValue}
                    onChange={(e) => updateRule(rule.id, { targetValue: e.target.value })}
                    placeholder="value..."
                    className="text-sm border rounded px-2 py-1 flex-1 min-w-[100px]"
                  />
                );
              })()}
              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(e) => updateRule(rule.id, { enabled: e.target.checked })}
                  className="rounded border-gray-300"
                />
                On
              </label>
              <button
                onClick={() => removeRule(rule.id)}
                className="text-red-400 hover:text-red-600 text-xs px-1"
                title="Remove rule"
              >✕</button>
              {tested && matchCounts.has(rule.id) && (
                <Badge variant="info">{matchCounts.get(rule.id)} match{matchCounts.get(rule.id) !== 1 ? 'es' : ''}</Badge>
              )}
            </div>
          </div>
        ))}

        {rules.length === 0 && (
          <div className="border-2 border-dashed rounded-lg p-6 text-center text-gray-400">
            <p className="text-sm">No rules defined yet</p>
            <p className="text-xs mt-1">Add rules to populate fields based on conditions, or skip to proceed without rules</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={addRule}>
          + Add Rule
        </Button>
      </div>

      {/* Exclusion Rules */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Exclusion Rules</h3>
            <p className="text-xs text-gray-400">Skip entries that match these conditions during import</p>
          </div>
          <Button variant="secondary" size="sm" onClick={addExclusionRule}>
            + Add Exclusion
          </Button>
        </div>
        <div className="space-y-3">
          {exclusionRules.map((rule) => (
            <div key={rule.id} className={`border border-red-200 bg-red-50/30 rounded-lg p-3 ${!rule.enabled ? 'opacity-40' : ''}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-red-600 w-16">SKIP IF</span>
                <select
                  value={rule.sourceField}
                  onChange={(e) => updateExclusionRule(rule.id, { sourceField: e.target.value })}
                  className="text-sm border rounded px-2 py-1"
                >
                  {SOURCE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select
                  value={rule.condition}
                  onChange={(e) => updateExclusionRule(rule.id, { condition: e.target.value as FieldRule['condition'] })}
                  className="text-sm border rounded px-2 py-1"
                >
                  {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {!noValue(rule.condition) && (
                  rule.sourceField === 'productType' && productTypes && productTypes.length > 0 ? (
                    <select
                      value={rule.value}
                      onChange={(e) => updateExclusionRule(rule.id, { value: e.target.value })}
                      className="text-sm border rounded px-2 py-1 flex-1"
                    >
                      <option value="">Select type...</option>
                      {productTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={rule.value}
                      onChange={(e) => updateExclusionRule(rule.id, { value: e.target.value })}
                      placeholder="value..."
                      className="text-sm border rounded px-2 py-1 flex-1 min-w-[100px]"
                    />
                  )
                )}
                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(e) => updateExclusionRule(rule.id, { enabled: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  On
                </label>
                <button
                  onClick={() => removeExclusionRule(rule.id)}
                  className="text-red-400 hover:text-red-600 text-xs px-1"
                  title="Remove rule"
                >✕</button>
                {tested && matchCounts.has(rule.id) && (
                  <Badge variant="info">{matchCounts.get(rule.id)} match{matchCounts.get(rule.id) !== 1 ? 'es' : ''}</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {(rules.length > 0 || exclusionRules.length > 0) && (
          <Button variant="secondary" size="sm" onClick={handleTest}>
            Test Rules
          </Button>
        )}
        {tested && (
          <span className="text-xs text-gray-400">
            {Array.from(matchCounts.values()).reduce((a, b) => a + b, 0)} total matches
          </span>
        )}
      </div>

      <p className="text-xs bg-blue-50 text-blue-700 p-3 rounded">
        Field rules are applied in order and saved per brand. Exclusion rules skip matching entries during import.
        Use "Test Rules" to see match counts before applying.
      </p>

      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onSkip}>
            Skip Rules →
          </Button>
          <Button variant="secondary" size="sm" onClick={() => { setShowJson(true); setJsonLimit(10); }}>
            View JSON
          </Button>
        </div>
        <Button onClick={handleApply} disabled={loadingRules}>
          {(() => {
            const enabledFieldCount = rules.filter((r) => r.enabled).length;
            const enabledExclCount = exclusionRules.filter((r) => r.enabled).length;
            const total = enabledFieldCount + enabledExclCount;
            return total > 0
              ? `Apply ${total} Rule${total !== 1 ? 's' : ''} & Preview →`
              : 'Save & Preview →';
          })()}
        </Button>
      </div>

      {/* JSON Viewer */}
      {showJson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowJson(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium">Import Data JSON ({entries.length} entries)</h3>
              <button onClick={() => setShowJson(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-gray-900 text-green-400">
              {JSON.stringify(entries.slice(0, jsonLimit), null, 2)}
            </pre>
            <div className="px-4 py-2 border-t flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Showing {Math.min(jsonLimit, entries.length)} of {entries.length} entries
              </span>
              {jsonLimit < entries.length && (
                <button
                  onClick={() => setJsonLimit((prev) => Math.min(prev + 20, entries.length))}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                >
                  Load more ({Math.min(20, entries.length - jsonLimit)} more)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
